// A SCHEDULED activation, made legible (spec 01 §3.3.5, §9.16, D-261).
//
// THE BUG THIS EXISTS FOR. A BOM or routing version activated with a future
// `effective_from` is `status = active` on the wire and read "Active" on every
// screen — while §3.4 kept resolving TODAY to the older version. Nothing said
// "not yet". On the demo tenant 203 walkthrough activations ratcheted
// `KG-3x70`'s active date to 2030-06-10 and its standard cost read 2.23× the
// truth for weeks, with an "Active" badge on the version that was not in force.
// D-261 adds the back-out (withdraw); this file is the vocabulary the two
// editors share so the state is NAMED before it is undone.
//
// Pure, so the unit test can pin the boundaries:
//   · `isScheduledVersion` is the client mirror of the API's `decideWithdrawal`
//     (shared/version-activation.ts): `active` AND `effective_from > today`.
//     A null date fails CLOSED (not scheduled) — the server refuses a null the
//     same way, and a control offered on a row the server will refuse is worse
//     than no control.
//   · "today" is the caller's business date. Which calendar answers it is the
//     D-089 rule — data selection is facility-local — and the screens derive it
//     from the facility's timezone where one is in hand (lib/business-dates).
//     The server re-validates in the tenant's timezone and answers
//     VERSION_IN_FORCE when the calendars disagree at a day boundary; the
//     dialog renders that refusal specifically, never as a generic toast.
//   · `archivePreview` is the client mirror of §3.3.5's Archive rule as
//     amended (D-261, 2026-09-08): `superseded → archived` is refused while
//     the row's OWN `effective_to ≥ today` — no lookup of the current active
//     version, which may be a LATER successor and would name the wrong row.
//     The dialog says so before the press; the server stays the record.

import { RequestError } from "../../../../lib/request";

export type VersionStatus = "draft" | "active" | "superseded" | "archived";

export interface ScheduleVersionShape {
  id: string;
  version: number;
  status: string;
  effective_from: string | null;
  effective_to: string | null;
  /** BOM summaries carry `string | null` (group-wide = null); routings `string`. */
  facility_id: string | null;
}

/** `active` but not yet in force — `effective_from` strictly after `today`
 * (ISO dates compare lexicographically). Any other status, or no date, is
 * not scheduled. */
export function isScheduledVersion(
  status: string,
  effectiveFrom: string | null | undefined,
  today: string,
): boolean {
  if (status !== "active") return false;
  if (typeof effectiveFrom !== "string" || effectiveFrom.length === 0) return false;
  return effectiveFrom > today;
}

/** The calendar day before an ISO date, in UTC arithmetic (a date has no
 * zone; the same helper the API's activation uses to close the predecessor). */
export function previousDayIso(dateIso: string): string {
  const [y, m, d] = dateIso.split("-").map(Number);
  const prev = new Date(Date.UTC(y ?? 1970, (m ?? 1) - 1, (d ?? 1) - 1));
  return `${String(prev.getUTCFullYear()).padStart(4, "0")}-${String(prev.getUTCMonth() + 1).padStart(2, "0")}-${String(prev.getUTCDate()).padStart(2, "0")}`;
}

/**
 * The version a withdrawal is expected to RESTORE — a preview for the dialog.
 *
 * The server restores the `from_version_id` of the version's ACTIVATION row in
 * the change log (§3.3.5 — the OLDEST row pointing at it). The client has no
 * change-log read on this screen, but activation closes the predecessor
 * deterministically: `status = superseded, effective_to = effective_from − 1
 * day` in the same facility scope (§3.3.4). So the sibling whose
 * `effective_to` is the day before this version's `effective_from`, in the
 * same scope, IS that row's `from_version_id` — and its STATUS says what the
 * server will do with it:
 *
 *   · `restores`      — exactly one such sibling, still `superseded`: it comes
 *                       back to active.
 *   · `notRestorable` — the sibling(s) closed that day are `archived`
 *                       (housekeeping ran while this version was scheduled —
 *                       the trap §3.3.5's VERSION_STILL_RESUMABLE now refuses,
 *                       but rows archived before that rule exist). The server
 *                       answers PREDECESSOR_NOT_RESTORABLE; the dialog says so
 *                       BEFORE the human types a reason, instead of promising
 *                       "nothing will be restored" — which is a different
 *                       fact, and the wrong one.
 *   · `none`          — nothing was closed that day: the version superseded
 *                       nothing, and the product will be left with no active
 *                       version — legal (§3.4 answers NO_EFFECTIVE_BOM), said
 *                       up front rather than discovered afterwards.
 *   · `unknown`       — the screen cannot tell (no date in hand yet, or more
 *                       than one superseded candidate). Says so; the server
 *                       is the record and the result banner names what it
 *                       actually restored.
 *
 * The server stays the record either way: the result banner states
 * `restored_version_id` as returned, never this preview.
 */
export type RestorePreview<V> =
  | { kind: "restores"; version: V }
  | { kind: "notRestorable"; version: V }
  | { kind: "none" }
  | { kind: "unknown" };

export function restorePreview<V extends ScheduleVersionShape>(
  versions: readonly V[],
  withdrawing: Pick<ScheduleVersionShape, "id" | "effective_from" | "facility_id">,
): RestorePreview<V> {
  if (withdrawing.effective_from === null || withdrawing.effective_from.length === 0) {
    return { kind: "unknown" };
  }
  const boundary = previousDayIso(withdrawing.effective_from);
  const closedThatDay = versions.filter(
    (v) =>
      v.id !== withdrawing.id &&
      v.facility_id === withdrawing.facility_id &&
      v.effective_to === boundary,
  );
  const superseded = closedThatDay.filter((v) => v.status === "superseded");
  if (superseded.length === 1) {
    const [only] = superseded;
    if (only !== undefined) return { kind: "restores", version: only };
  }
  if (superseded.length > 1) return { kind: "unknown" };
  const archived = closedThatDay.filter((v) => v.status === "archived");
  if (archived.length > 0) {
    // Several archived rows closed on the same day can only come from a chain
    // unwound and re-scheduled more than once; the newest is the one the
    // activation row most plausibly names, and the sentence is the same.
    const newest = archived.reduce((best, v) => (v.version > best.version ? v : best));
    return { kind: "notRestorable", version: newest };
  }
  if (closedThatDay.length === 0) return { kind: "none" };
  return { kind: "unknown" };
}

/** The dialog's view of a preview: the version NUMBER, not the row. */
export type RestoreSummary =
  { kind: "restores" | "notRestorable"; version: number } | { kind: "none" | "unknown" };

export function toRestoreSummary(preview: RestorePreview<ScheduleVersionShape>): RestoreSummary {
  if (preview.kind === "restores" || preview.kind === "notRestorable") {
    return { kind: preview.kind, version: preview.version.version };
  }
  return { kind: preview.kind };
}

/** The three §3.3.5 refusals, each with its own sentence in the dialog. */
export type WithdrawRefusal = "inForce" | "notActive" | "predecessorNotRestorable";

const REFUSAL_BY_CODE: Readonly<Record<string, WithdrawRefusal>> = {
  VERSION_IN_FORCE: "inForce",
  VERSION_NOT_ACTIVE: "notActive",
  PREDECESSOR_NOT_RESTORABLE: "predecessorNotRestorable",
};

/** Which specific refusal a failed withdraw is, or null for anything else
 * (network, 403, 500 — rendered with the server's own title). */
export function withdrawRefusalOf(error: unknown): WithdrawRefusal | null {
  if (!(error instanceof RequestError)) return null;
  if (error.code === undefined) return null;
  return REFUSAL_BY_CODE[error.code] ?? null;
}

/**
 * What archiving a version MEANS, said before the human commits (§3.3.5
 * Archive — `draft → archived` abandons, `superseded → archived` housekeeps;
 * never deletes, never undone).
 *
 *   · `abandon`        — a draft: never activated, nothing depends on it.
 *   · `housekeep`      — a superseded version whose range has ended.
 *   · `stillResumable` — a superseded version whose range has NOT ended by
 *                        this screen's calendar (`effective_to ≥ today`): the
 *                        version that superseded it is not yet in force, and
 *                        withdrawing that scheduled version would bring this
 *                        one back. The server refuses this by name
 *                        (VERSION_STILL_RESUMABLE); the dialog says so up
 *                        front and keeps the control — the calendars can
 *                        disagree at a day boundary, and the server decides.
 *   · `null`           — any other status: archive is not offered at all
 *                        (active is superseded or withdrawn, never archived;
 *                        archived is terminal).
 *
 * The rule is stated on the row itself, exactly as the spec states it — a
 * superseded row's `effective_to` is always `successor.effective_from − 1`
 * (§3.3.4), so "range not ended" IS "successor not yet in force" without
 * naming the successor. A superseded row with no `effective_to` cannot be
 * judged here and is offered as housekeeping; the server's rule reads the
 * same null the same way.
 */
export type ArchivePreview =
  { kind: "abandon" } | { kind: "housekeep" } | { kind: "stillResumable"; until: string };

export function archivePreview(
  status: string,
  effectiveTo: string | null | undefined,
  today: string,
): ArchivePreview | null {
  if (status === "draft") return { kind: "abandon" };
  if (status !== "superseded") return null;
  if (typeof effectiveTo === "string" && effectiveTo.length > 0 && effectiveTo >= today) {
    return { kind: "stillResumable", until: effectiveTo };
  }
  return { kind: "housekeep" };
}

/** The two §3.3.5 archive refusals, each with its own sentence in the
 * archive dialog: the row is still what a withdrawal would restore, or its
 * status moved before the write (a concurrent withdraw restored it, or it was
 * activated) — `BOM_NOT_ARCHIVABLE` / `ROUTING_NOT_ARCHIVABLE` are one fact. */
export type ArchiveRefusal = "stillResumable" | "notArchivable";

const ARCHIVE_REFUSAL_BY_CODE: Readonly<Record<string, ArchiveRefusal>> = {
  VERSION_STILL_RESUMABLE: "stillResumable",
  BOM_NOT_ARCHIVABLE: "notArchivable",
  ROUTING_NOT_ARCHIVABLE: "notArchivable",
};

/** Which specific refusal a failed archive is, or null for anything else. */
export function archiveRefusalOf(error: unknown): ArchiveRefusal | null {
  if (!(error instanceof RequestError)) return null;
  if (error.code === undefined) return null;
  return ARCHIVE_REFUSAL_BY_CODE[error.code] ?? null;
}

/**
 * A date the API named when it refused — `today` (the calendar that decided
 * VERSION_IN_FORCE) and `effective_from` (the date it holds for the row).
 *
 * WIRE SHAPE. The API spreads an AppProblem's `extensions` as TOP-LEVEL
 * members of the problem body (apps/api/src/platform/problem.filter.ts —
 * RFC 9457 §3.2: extension members sit beside `type`/`title`/`status`, there
 * is no `extensions` envelope). The first cut of this file read
 * `problem.extensions.today`, a path that exists on no response, so the
 * "by the server's calendar today is …" sentence could never render; the
 * quality area recorded the same miss on its COA gaps
 * (quality/screens/coa-manager.tsx). Read the member where it actually is.
 */
function refusalMemberOf(
  error: unknown,
  member: "today" | "effective_from" | "effective_to" | "current_status",
): string | undefined {
  if (!(error instanceof RequestError) || error.problem === undefined) return undefined;
  const problem: Record<string, unknown> = error.problem;
  const value = problem[member];
  return typeof value === "string" && value.length > 0 ? value : undefined;
}

/** The `today` the API used when it refused VERSION_IN_FORCE or
 * VERSION_STILL_RESUMABLE. */
export function refusalTodayOf(error: unknown): string | undefined {
  return refusalMemberOf(error, "today");
}

/** The `effective_from` the API holds for the row it refused to withdraw. */
export function refusalEffectiveFromOf(error: unknown): string | undefined {
  return refusalMemberOf(error, "effective_from");
}

/** The `effective_to` the API holds for the row it refused to archive
 * (VERSION_STILL_RESUMABLE names the day the row's own range ends). */
export function refusalEffectiveToOf(error: unknown): string | undefined {
  return refusalMemberOf(error, "effective_to");
}

/** The status the API found on the row when it refused *_NOT_ARCHIVABLE,
 * sent as `current_status`. A problem body's own `status` is the HTTP status. */
export function refusalStatusOf(error: unknown): string | undefined {
  return refusalMemberOf(error, "current_status");
}
